import React, { useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { connect } from 'react-redux';
import cx from 'classnames';

// Util
import { useForceUpdateOnWindowResize } from '@zola/zola-ui/src/hooks/useForceUpdateOnWindowResize';
import { notMobile } from '@zola-helpers/client/dist/es/util/responsive';
import { filterVideos, filterStories } from 'util/filterEntities';

// Types
import type {
  WPublicWeddingView,
  WCmsHomepageSectionView,
  WWeddingAccountView,
  WWeddingView,
  WCmsVideoView,
} from '@zola/svc-web-api-ts-client';
import type { RootState, AppDispatch } from 'reducers/index';

// Actions
import { getWeddingPageByTypeV2 } from 'actions/PublicWebsiteActions';
import { resetEntities } from 'actions/website/EntityActions';
import { getWeddingThemeKey } from 'selectors/website/websiteSelectors';
import { getPublicWebsiteWedding } from 'selectors/public/publicWebsiteSelectors';
import { getLocalStorage } from '@zola-helpers/client/dist/es/util/storage';
import VideoSnippet from 'components/util/VideoSnippet';
import type { PageType, HomeFormData, WeddingPropType } from './types';

// Components
import Hero from './components/Hero';
import NameSectionMiddle from './components/NameSectionMiddle';
import RightNameSection from './components/RightNameSection';
import LeftNameSection from './components/LeftNameSection';
import SealSection from './components/SealSection';
import WeddingInfoSection from './components/WeddingInfoSection';
import HomepageSections from './components/HomepageSections';

dayjs.extend(utc);

type HomeStateProps = {
  entities?: Array<WCmsVideoView | WCmsHomepageSectionView>;
  wedding: WeddingPropType;
  busy?: boolean;
  page?: PageType;
  weddingAccountId?: string;
  themeKey?: string;
};

type HomeOwnProps = {
  forPreview?: boolean;
  page?: PageType;
  formData?: HomeFormData;
};

const mapStateToProps = (state: RootState, ownProps: HomeOwnProps): HomeStateProps => ({
  entities: ownProps.forPreview
    ? state.website.entities.orderedIds.map(id => state.website.entities.byId[id])
    : state.publicWebsite.entities,
  wedding: ownProps.forPreview
    ? Object.assign({}, state.wedding.wedding, state.wedding.account)
    : state.publicWebsite.wedding,
  page: ownProps.forPreview ? ownProps.page : state.publicWebsite.page,
  busy: state.publicWebsite.busy,
  themeKey: ownProps.forPreview
    ? getWeddingThemeKey(state)
    : (getPublicWebsiteWedding(state) as WPublicWeddingView).theme?.key,
});

type HomeDispatchProps = {
  getHomepageSectionEntities: () => void;
  unloadEntities: () => void;
};

const mapDispatchToProps = (dispatch: AppDispatch): HomeDispatchProps => ({
  getHomepageSectionEntities: () => dispatch(getWeddingPageByTypeV2('home')),
  unloadEntities: () => dispatch(resetEntities()),
});

type HomeProps = HomeOwnProps & HomeStateProps & HomeDispatchProps;

const Home = ({
  entities = [],
  wedding,
  page,
  forPreview,
  busy,
  formData,
  themeKey: themeKeyProp,
  getHomepageSectionEntities,
  unloadEntities,
}: HomeProps): JSX.Element => {
  const themeKey = getLocalStorage('WEBSITE_THEME_OVERRIDE') || themeKeyProp;
  const pageBody = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!forPreview) getHomepageSectionEntities();

    return function cleanup() {
      if (!forPreview) unloadEntities();
    };
  }, []);
  useForceUpdateOnWindowResize();

  const handleScroll = () => {
    if (pageBody.current) {
      pageBody.current.scrollIntoView();
    }
  };

  const homepageSections = entities.filter(filterStories);
  const videos = (entities as WCmsVideoView[]).filter(filterVideos);

  const headerStyle = page?.header_image_url
    ? { backgroundImage: `url(${page?.header_image_url}?h=${notMobile() ? 1400 : 700})` }
    : {};
  const footerStyle = page?.footer_image_url
    ? { backgroundImage: `url(${page?.footer_image_url}?h=${notMobile() ? 1400 : 700})` }
    : {};
  const welcomeMessage = page?.title;
  // set values to default for what's stored on the wedding object from Redux
  let { hashtag } = wedding;
  let weddingCity = wedding.city;
  let weddingStateProvince = wedding.state_province;
  let weddingDate: dayjs.Dayjs | undefined = wedding.wedding_date
    ? dayjs.utc(wedding.wedding_date)
    : undefined;
  if (forPreview) {
    // if the form exists, map those values so the couple sees live updates
    // eslint-disable-next-line prefer-destructuring
    if (formData?.hashtag) hashtag = formData.hashtag;
    if (formData?.city) weddingCity = formData.city;
    if (formData?.stateProvince) weddingStateProvince = formData.stateProvince;

    if (formData?.title) {
      // hacky for now, but when the title is passed, we are in a preview NOT on the customization dashboard
      // TODO: re-factor when WebsitePreview logic cleaned up
      weddingDate = dayjs.utc(formData.weddingDate);
    }
  }

  // conditions for classes
  const missingEntities = entities.length === 0;
  const hasImages = !!page?.header_image_url;
  const hasWelcomeMessage = !!welcomeMessage;
  // classes
  const containerClassName = cx('public-page', {
    'offset-background-image': page?.title && !page.header_image_url,
  });
  const contentClassName = cx('page-content', {
    'no-entities': missingEntities,
    'has-entities': !missingEntities,
    'has-image': hasImages,
    'no-image-above': !hasImages,
    'has-welcome-message': hasWelcomeMessage,
    'no-welcome-message': !hasWelcomeMessage,
  });

  const renderVideo = () => {
    if (videos.length) {
      const { url, caption, id } = videos[0]; // https://media2.giphy.com/media/9Jmb2idg10qJSygvTQ/200.gif
      return (
        <VideoSnippet
          url={url}
          caption={caption}
          id={id}
          key={id}
          additionalPaddingBottom={!homepageSections.length}
        />
      );
    }
    return null;
  };

  return (
    <div className={containerClassName}>
      {page &&
        !busy &&
        ((wedding as WPublicWeddingView).wedding_account_id ||
          (wedding as WWeddingAccountView & WWeddingView).account_id) && (
          <div>
            {(page.header_image_url || page.title) && (
              <Hero
                page={page}
                wedding={wedding}
                headerStyle={headerStyle}
                handleScroll={handleScroll}
              />
            )}
            <div className={contentClassName}>
              <div className="page-accent-top-left" />
              <div className="page-accent-top-right" />
              <div className="page-accent-bottom-left" />
              <div className="page-accent-bottom-right" />
              {wedding.owner_first_name && wedding.partner_first_name && wedding.enable_seal ? (
                <SealSection topSeal wedding={wedding} />
              ) : null}
              <div className="names-section" ref={pageBody}>
                <div className="names-accent-top-left" />
                <div className="names-accent-top-right" />
                <div className="names-accent-bottom-left" />
                <div className="names-accent-bottom-right" />
                <LeftNameSection wedding={wedding} />
                <NameSectionMiddle wedding={wedding} />
                <RightNameSection wedding={wedding} />
              </div>
              {(weddingDate ||
                weddingCity ||
                weddingStateProvince ||
                hashtag ||
                videos.length > 0) && (
                <WeddingInfoSection
                  wedding={wedding}
                  weddingDate={weddingDate}
                  weddingCity={weddingCity}
                  weddingStateProvince={weddingStateProvince}
                  hashtag={hashtag}
                  themeKey={themeKey}
                  renderVideo={renderVideo}
                />
              )}
              {homepageSections.length > 0 && (
                <HomepageSections homepageSections={homepageSections} />
              )}
              {wedding.owner_first_name && wedding.partner_first_name && wedding.enable_seal && (
                <SealSection topSeal={false} wedding={wedding} />
              )}
            </div>
            {window.zola.env !== 'production' ? (
              <span>
                {page.footer_image_url && (
                  <div
                    role="img"
                    aria-label={`The Wedding Website of ${wedding.owner_first_name} ${wedding.owner_last_name} and ${wedding.partner_first_name} ${wedding.partner_last_name}`}
                    className="bottom-section"
                    style={footerStyle}
                  />
                )}
              </span>
            ) : (
              <span>
                {page.footer_image_url && <div className="bottom-section" style={footerStyle} />}
              </span>
            )}
          </div>
        )}
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Home);
